import { emitMeaningfulAction, emitReviewMoment } from '@/lib/meaningfulActions';
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useNativePlatform } from '@/hooks/useNativePlatform';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Send, ArrowRight, Shield, Clock } from 'lucide-react';
import { useUserSubscription } from '@/hooks/useUserSubscription';
import { useNavigate } from 'react-router-dom';
import { usePrePublishModeration } from '@/hooks/usePrePublishModeration';
import { sanitizeAndValidate } from '@/lib/security';
import { cn } from '@/lib/utils';
import { detectPII, hasCriticalPII } from '@/lib/piiDetection';
import PIIWarningDialog from '@/components/PIIWarningDialog';
import SelfHarmSupportDialog from '@/components/moderation/SelfHarmSupportDialog';
import * as analytics from '@/lib/analytics';
import { bucketCharCount } from '@/lib/analytics-events';
import { recordNotificationAction } from '@/lib/notificationEngagement';


interface CreateVentProps {
  onVentCreated?: () => void;
  onSuccess?: () => void;
  onCancel?: () => void;
  defaultRoomId?: string;
  lockToRoom?: boolean;
}

const CreateVent: React.FC<CreateVentProps> = ({
  onVentCreated,
  onSuccess,
  onCancel,
  defaultRoomId,
  lockToRoom = false
}) => {
  const isNative = useNativePlatform();
  const [content, setContent] = useState('');
  const [selectedRoom, setSelectedRoom] = useState('');
  const [rooms, setRooms] = useState<Array<{id: string, name: string}>>([]);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();
  const { user } = useAuth();
  const { getRoomLimit } = useUserSubscription();
  const navigate = useNavigate();
  
  const [selfHarmOpen, setSelfHarmOpen] = useState(false);
  const { checkContent, logPublishedContent, isChecking } = usePrePublishModeration({
    onSelfHarm: () => setSelfHarmOpen(true),
    onHeld: (holdId) => {
      console.log('Content held for review:', holdId);
    },
    onRejected: (reason) => {
      console.log('Content rejected:', reason);
    }
  });

  // PII detection state
  const [piiDialogOpen, setPiiDialogOpen] = useState(false);
  const [piiResult, setPiiResult] = useState<{ detectedTypes: string[]; redactedContent: string } | null>(null);
  const [pendingSanitized, setPendingSanitized] = useState('');
  const [feedbackTag, setFeedbackTag] = useState<string | null>(null);
  const [composerExpanded, setComposerExpanded] = useState(false);

  const FEEDBACK_TAGS = [
    { value: 'just_venting', label: 'Just Venting' },
    { value: 'advice_wanted', label: 'Advice Wanted' },
    { value: 'encouragement', label: 'Words of Encouragement' },
    { value: 'perspective', label: 'Perspective Needed' },
  ];

  // Fetch user's joined rooms (limited by their plan)
  useEffect(() => {
    const fetchRooms = async () => {
      if (!user) return;

      if (lockToRoom && defaultRoomId) {
        const { data, error } = await supabase
          .from('rooms')
          .select('id, name')
          .eq('id', defaultRoomId)
          .single();

        if (!error && data) {
          setRooms([data]);
          setSelectedRoom(defaultRoomId);
        }
        return;
      }

      const { data: memberships, error: membershipError } = await supabase
        .from('room_members')
        .select('room_id')
        .eq('user_id', user.id);

      if (membershipError) {
        console.error('Error fetching memberships:', membershipError);
        return;
      }

      const roomIds = (memberships || []).map(m => m.room_id).filter(Boolean);

      if (roomIds.length === 0) {
        setRooms([]);
        return;
      }

      const { data: roomsData, error: roomsError } = await supabase
        .from('rooms')
        .select('id, name')
        .in('id', roomIds);

      if (roomsError) {
        console.error('Error fetching rooms by ids:', roomsError);
        return;
      }

      const userRooms = (roomsData || [])
        .filter(r => r.name !== 'Vent: Question of the Day')
        .map(r => ({ id: r.id, name: r.name }))
        .sort((a, b) => a.name.localeCompare(b.name));
      
      setRooms(userRooms);
      
      if (!selectedRoom) {
        if (defaultRoomId && userRooms.find(room => room.id === defaultRoomId)) {
          setSelectedRoom(defaultRoomId);
        } else if (userRooms.length > 0) {
          setSelectedRoom(userRooms[0].id);
        }
      }
    };

    fetchRooms();
  }, [user, defaultRoomId, lockToRoom, getRoomLimit]);

  // Core submit logic (after PII check)
  const submitVent = async (finalContent: string) => {
    if (!user) return;

    setLoading(true);
    try {
      const { canPublish, result } = await checkContent(finalContent, 'vent', {
        roomId: selectedRoom
      });

      if (!canPublish) {
        setLoading(false);
        if (result?.action === 'held') {
          setContent('');
        }
        return;
      }

      const { data: ventData, error } = await supabase
        .from('vents')
        .insert([
          {
            user_id: user.id,
            content: finalContent,
            room_id: selectedRoom,
            feedback_tag: feedbackTag,
          },
        ])
        .select()
        .single();

      if (error) throw error;

      if (ventData) {
        logPublishedContent(finalContent, 'vent', ventData.id);
        analytics.track('Vent Posted', {
          room_id: selectedRoom,
          char_count_bucket: bucketCharCount(finalContent.length),
        });
        analytics.track('vent_posted', {
          topic: selectedRoom ?? 'general',
          posting_method: 'compose',
          character_count: finalContent.length,
          attachment_type: 'none',
        });
        void recordNotificationAction();
        // Vent Streak: posting a vent counts as a check-in
        const { recordVentStreakActivity } = await import('@/lib/recordVentStreakActivity');
        void recordVentStreakActivity();
        const { recordOrgActivity } = await import('@/lib/orgActivity');
        void recordOrgActivity('vent', { room: selectedRoom || null });
      }

      // Process mentions
      const mentionRegex = /@(\w+)/g;
      const mentions = finalContent.match(mentionRegex);
      
      if (mentions && mentions.length > 0) {
        const usernames = mentions.map(m => m.substring(1));
        const { data: mentionedProfiles } = await supabase
          .from('profiles')
          .select('user_id, username')
          .in('username', usernames);
        
        if (mentionedProfiles) {
          for (const profile of mentionedProfiles) {
            try {
              await supabase.functions.invoke('send-notification-event', {
                body: {
                  type: 'mention',
                  senderId: user.id,
                  contentId: profile.user_id,
                  contentType: 'vent',
                  ventId: ventData.id,
                  contentPreview: finalContent.substring(0, 100)
                }
              });
            } catch (mentionError) {
              console.error('Failed to send mention notification:', mentionError);
            }
          }
        }
      }

      // Send room vent notification to other room members (fire-and-forget)
      if (selectedRoom && ventData) {
        supabase.functions.invoke('send-notification-event', {
          body: {
            type: 'room_vent',
            senderId: user.id,
            contentId: ventData.id,
            ventId: ventData.id,
            roomId: selectedRoom,
            contentPreview: finalContent.substring(0, 100),
          }
        }).catch(err => console.error('Failed to send room vent notification:', err));
      }

      toast({
        title: 'Vent posted!',
        description: 'Your anonymous vent has been shared.',
      });

      setContent('');
      setFeedbackTag(null);
      onVentCreated?.();
      emitMeaningfulAction('vent_posted');
      onSuccess?.();
      
    } catch (error: any) {
      toast({
        variant: 'destructive',
        title: 'Error posting vent',
        description: error.message || 'An unexpected error occurred.',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user) {
      toast({
        variant: 'destructive',
        title: 'Authentication required',
        description: 'You must be logged in to create a vent.',
      });
      return;
    }

    const { sanitized, error: validationError } = sanitizeAndValidate(content, 2000);
    if (validationError) {
      toast({
        variant: 'destructive',
        title: 'Invalid input',
        description: validationError,
      });
      return;
    }

    // PII detection check
    const pii = detectPII(sanitized);
    if (pii.hasPII) {
      setPendingSanitized(sanitized);
      setPiiResult({ detectedTypes: pii.detectedTypes, redactedContent: pii.redactedContent });
      setPiiDialogOpen(true);
      return;
    }

    await submitVent(sanitized);
  };

  const handlePIIEdit = () => {
    setPiiDialogOpen(false);
    setPiiResult(null);
  };

  const handlePIIPostRedacted = async () => {
    setPiiDialogOpen(false);
    if (piiResult) {
      await submitVent(piiResult.redactedContent);
    }
    setPiiResult(null);
  };

  const handlePIIPostAnyway = async () => {
    setPiiDialogOpen(false);
    await submitVent(pendingSanitized);
    setPiiResult(null);
  };

  return (
    <>
      <SelfHarmSupportDialog open={selfHarmOpen} onOpenChange={setSelfHarmOpen} />
      <PIIWarningDialog
        open={piiDialogOpen}
        detectedTypes={piiResult?.detectedTypes || []}
        redactedPreview={piiResult?.redactedContent || ''}
        allowPostAnyway={!hasCriticalPII(pendingSanitized)}
        onEdit={handlePIIEdit}
        onPostRedacted={handlePIIPostRedacted}
        onPostAnyway={handlePIIPostAnyway}
      />
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="mb-2 flex items-center gap-1.5">
            <p className="text-sm font-semibold">Share Your Thoughts</p>
            <span title="AI-moderated for safety">
              <Shield size={13} className="text-muted-foreground" />
            </span>
          </div>
          <form onSubmit={handleSubmit} className="space-y-3">
            <div className="space-y-3">
              {!lockToRoom && (
                <div>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 max-w-xs">
                      {isNative ? (
                        <select
                          value={selectedRoom}
                          onChange={(e) => setSelectedRoom(e.target.value)}
                          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                        >
                          <option value="" disabled>Select a room</option>
                          {rooms.map((room) => (
                            <option key={room.id} value={room.id}>{room.name}</option>
                          ))}
                        </select>
                      ) : (
                        <Select value={selectedRoom} onValueChange={setSelectedRoom}>
                          <SelectTrigger className="bg-background">
                            <SelectValue placeholder="Select a room" />
                          </SelectTrigger>
                          <SelectContent>
                            {rooms.map((room) => (
                              <SelectItem key={room.id} value={room.id} className="cursor-pointer">
                                {room.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => selectedRoom && navigate(`/app/rooms/${selectedRoom}`)}
                      disabled={!selectedRoom}
                      className="shrink-0 border border-border"
                      aria-label="Enter Room"
                      title="Enter Room"
                    >
                      <ArrowRight size={14} />
                    </Button>
                  </div>
                </div>
              )}
              
              {lockToRoom && rooms.length > 0 && (
                <div>
                  <label className="text-sm font-medium mb-2 block">Posting to</label>
                  <div className="px-3 py-2 bg-muted rounded-md text-sm text-muted-foreground">
                    {rooms[0].name}
                  </div>
                </div>
              )}
              
              <div className="space-y-3">
                <Textarea
                  placeholder="What's on your mind? Share anonymously..."
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  onFocus={() => setComposerExpanded(true)}
                  onBlur={() => {
                    if (!content.trim()) setComposerExpanded(false);
                  }}
                  rows={1}
                  className={cn(
                    'resize-none transition-[min-height] duration-200 ease-out',
                    composerExpanded ? 'min-h-[150px] py-2' : 'min-h-[38px] py-1.5'
                  )}
                />


                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="text-[11px] text-muted-foreground">Feedback wanted (optional)</span>
                  {FEEDBACK_TAGS.map((tag) => (
                    <button
                      key={tag.value}
                      type="button"
                      onClick={() => setFeedbackTag(feedbackTag === tag.value ? null : tag.value)}
                      className={`rounded-full border px-2.5 py-1 text-[11px] transition-colors ${
                        feedbackTag === tag.value
                          ? 'border-primary bg-primary text-primary-foreground'
                          : 'border-border bg-muted text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                      }`}
                    >
                      {tag.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              {onCancel && (
                <Button type="button" variant="outline" onClick={onCancel} className="flex-1">
                  Cancel
                </Button>
              )}
              <Button
                type="submit"
                disabled={loading || isChecking || !content.trim()}
                className={`flex items-center gap-2 ${onCancel ? 'flex-1' : ''}`}
              >
                {isChecking ? (
                  <>
                    <Shield size={16} className="animate-pulse" />
                    Checking...
                  </>
                ) : loading ? (
                  <>
                    <Clock size={16} className="animate-spin" />
                    Posting...
                  </>
                ) : (
                  <>
                    <Send size={16} />
                    Post Vent
                  </>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </>
  );
};

export default CreateVent;
export { CreateVent };

